"use client";
import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslations } from "next-intl";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as Yup from "yup";

import Button from "@/components/ui/Button";
import FormField from "@/components/ui/Common/Forms/FormField";
import { Divider } from "@/components/ui/Divider";
import { ModalHeader } from "@/components/widgets";
import useInputValidation from "@/hooks/useInputValidation";
import { useSafeTranslations } from "@/hooks/useSafeTranslations";
import { useRouter } from "@/i18n/routing";
import { userService } from "@/services";
import { useModalStore } from "@/store/modal.store";
import { ChangeUserPassword } from "@/types/userInfo.type";

type FormValues = {
  password: string;
  newPassword: string;
  confirmNewPassword: string;
};

const PasswordResetForm = () => {
  const t = useTranslations();
  const tRealtor = useSafeTranslations("realtor");
  const { closeModal } = useModalStore();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { passwordLightSchema, passwordNewConfirmSchema } =
    useInputValidation();

  const methods = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      password: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    resolver: yupResolver(
      Yup.object({
        password: Yup.string().required(t("Required")),
        newPassword: passwordLightSchema,
        confirmNewPassword: passwordNewConfirmSchema,
      }).required(),
    ),
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { isValid, isDirty, errors },
  } = methods;

  const newPassword = watch("newPassword");

  useEffect(() => {
    if (newPassword) {
      trigger("confirmNewPassword");
    }
  }, [newPassword, trigger]);

  const handleFormSubmit = async (data: FormValues) => {
    try {
      const body: ChangeUserPassword = {
        oldPassword: data.password,
        newPassword: data.newPassword,
      };
      await userService.updateUserPassword(body);
      toast.success(t("profileFormProfileUpdateSuccess"));
      closeModal();
      reset();
    } catch (error) {
      console.error("Failed to update password:", error);

      if (error?.status === 401) {
        toast.error(
          tRealtor("profileOldPasswordIncorrect") ||
            "Failed to update profile. Please try again.",
        );
      } else {
        toast.error(
          tRealtor("profileFormProfileUpdateError") ||
            "Failed to update profile. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-8 min-w-[22rem] md:min-w-[30rem] lg:min-w-[40rem] w-full">
      <ModalHeader title={t("changePassword")} />

      <Divider />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="mb-8 mt-8 flex flex-col gap-8">
            <FormField
              {...register("password")}
              type={"password"}
              placeholder={t("password")}
              error={errors.password?.message}
            />
            <FormField
              {...register("newPassword")}
              type={"password"}
              placeholder={t("newPassword")}
              error={errors.newPassword?.message}
            />
            <FormField
              {...register("confirmNewPassword")}
              type={"password"}
              placeholder={t("confirmNewPassword")}
              error={errors.confirmNewPassword?.message}
            />
          </div>

          <Divider />

          <div className="search-panel__modal-footer">
            <Button
              type="submit"
              className="w-full md:w-auto min-w-[15rem] xl:min-w-[22rem] min-h-[5rem] mx-auto"
              size="md"
              color="red"
              radius="md"
              disabled={!isDirty || !isValid || isLoading}
            >
              {t("save")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default PasswordResetForm;
